import { z } from 'zod';
import { Priority, Severity, TicketStatus, TicketType, TicketSource, BugOutcome, } from './enums.js';
import { TagRef } from './tag.js';
/** Customer/contact info captured on intake (legacy `reporterContact`). */
export const ReporterContact = z.object({
    name: z.string().min(1).optional(),
    /** Waypoint sends `fullName`; kept alongside `name` for compatibility. */
    fullName: z.string().min(1).optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    organization: z.string().optional(),
    externalUserId: z.string().optional(),
    customerId: z.string().optional(),
    customerName: z.string().optional(),
});
/**
 * Full internal ticket projection (NOT customer-safe).
 * The customer-safe shape is `CustomerSafeTicket` below.
 */
export const Ticket = z.object({
    workspaceKind: z.enum(['product', 'support']).nullable().optional(),
    workflowStageId: z.string().nullable().optional(),
    workflowVersion: z.number().optional(),
    workflowEnteredAt: z.string().nullable().optional(),
    workflowOwnerId: z.string().nullable().optional(),
    workflowWaitingReason: z.string().nullable().optional(),
    workflowReviewState: z.string().optional(),
    workflowReadyAt: z.string().nullable().optional(),
    workflowHandedOffAt: z.string().nullable().optional(),
    workflowNextAction: z.string().nullable().optional(),
    workflowFollowUpAt: z.string().nullable().optional(),
    workflowExpedite: z.boolean().optional(),
    workflowCustomerReplyAt: z.string().nullable().optional(),
    workflowSupportReplyAt: z.string().nullable().optional(),
    id: z.string(),
    code: z.string(), // ATL-000001
    type: TicketType,
    status: TicketStatus,
    title: z.string(),
    description: z.string().nullable(), // rich text (HTML/JSON from editor)
    priority: Priority,
    severity: Severity.nullable(),
    source: TicketSource,
    projectId: z.string().nullable(),
    projectKey: z.string().nullable(),
    parentId: z.string().nullable(), // hierarchy parent (epic -> feature -> story)
    productArea: z.string().nullable(),
    component: z.string().nullable(),
    repo: z.string().nullable(), // GitHub repo ("owner/name") this ticket targets
    assigneeIds: z.array(z.string()).default([]),
    // Outstanding next-action holders, including balls currently on hold.
    ballHolders: z.array(z.object({ userId: z.string(), userName: z.string() })).default([]),
    requesterIds: z.array(z.string()).default([]), // users who requested this ticket (many-to-many)
    customerIds: z.array(z.string()).default([]), // linked Customer entity ids (many-to-many)
    reporterId: z.string().nullable(),
    reporterContact: ReporterContact.nullable(),
    customerId: z.string().nullable(), // legacy free-text/tenant scalar
    customerName: z.string().nullable(), // legacy free-text scalar
    sprintId: z.string().nullable(),
    releaseId: z.string().nullable(),
    promiseId: z.string().nullable(), // customer commitment this ticket fulfills
    blockerId: z.string().nullable(), // migration gate this ticket helps clear
    storyPoints: z.number().nullable(),
    estimateHours: z.number().nullable(),
    remainingHours: z.number().nullable(),
    impactArr: z.number().nullable(), // impacted annual recurring revenue ($); $0 is valid
    impactLtv: z.number().nullable(), // lifetime value ($); $0 is valid
    impactTcv: z.number().nullable(), // total contract value ($); $0 is valid
    projectedArr: z.number().nullable(), // the FEATURE's projected ARR ($, manual); drives the value/effort matrix
    dueDate: z.string().nullable(), // ISO date
    rank: z.string().nullable(), // lexorank-style stable ordering
    boardRank: z.string().nullable().default(null), // manual board-column sequence (global); null = unsequenced
    labels: z.array(z.string()).default([]), // legacy free-text labels (migrating to tags)
    tags: z.array(TagRef).default([]), // governed master tags
    environment: z.string().nullable(),
    externalId: z.string().nullable(),
    isLegacy: z.boolean().default(false), // legacy (pre-Tracx) item — filterable
    customSystem: z.boolean().default(false), // legacy item that lives in a custom (non-tracxtms) system
    archivedAt: z.string().nullable(), // set when a done item is archived
    approvedToPromote: z.boolean().default(false), // done item cleared to advance
    blocked: z.boolean().default(false), // rolls up to a "Blocked" tag on the customer
    attritionRating: z.number().int().min(1).max(5).nullable(), // customer attrition risk 1–5; null = not set
    attritionNotes: z.string().nullable(), // context for the attrition rating (<=250 chars)
    resolvedAt: z.string().nullable(), // set when the ticket entered `done`; null otherwise
    attritionArrLoss: z.number().nullable(), // estimated ARR loss ($) if the customer churns — required context once a rating is set
    devCount: z.number().int().min(1).default(1), // developers planned on the task (estimateHours is per developer)
    // ── IRA customer-health cache (batch-synced; read-only — no patch fields) ──
    healthScore: z.number().int().min(0).max(100).nullable(), // 0–100 from IRA
    healthBand: z.string().nullable(), // critical | at-risk | moderate | healthy
    iraSyncedAt: z.string().nullable(), // freshness of healthScore/impactArr
    // ── AI dev estimate provenance (batch job; read-only — no patch fields) ──
    aiEstimateHours: z.number().nullable(),
    aiEstimateNotes: z.string().nullable(),
    aiEstimatedAt: z.string().nullable(),
    aiImpactArr: z.number().nullable(), // AI-estimated revenue impact ($, features) — read-only
    aiImpactArrNotes: z.string().nullable(),
    aiImpactArrAt: z.string().nullable(),
    // ── Bug outcome (internal_bug only): human-confirmed value + the AI suggestion ──
    bugOutcome: BugOutcome.nullable().optional(),
    bugOutcomeSetBy: z.string().nullable().optional(), // user id of who set it
    bugOutcomeSetAt: z.string().nullable().optional(),
    aiBugOutcome: BugOutcome.nullable().optional(), // null with aiBugOutcomeAt set = the AI declined to guess
    aiBugOutcomeReason: z.string().nullable().optional(),
    aiBugOutcomeAt: z.string().nullable().optional(),
    // ── Testing & Proof: a rich-HTML section (paragraph of what was done to verify
    // the work + inline screenshots proving the acceptance criteria in `description`).
    // Written by a human or the AI agent. null = not yet recorded.
    testProof: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Customer-safe ticket projection (BR-003 #7, FR-§13.1).
 * Dedicated projection — never derived by ad-hoc field deletion.
 * Exposes only fields safe for external API consumers.
 */
export const CustomerSafeTicket = z.object({
    id: z.string(),
    code: z.string(),
    type: TicketType,
    status: TicketStatus,
    title: z.string(),
    priority: Priority,
    severity: Severity.nullable(),
    project: z
        .object({ id: z.string(), key: z.string(), name: z.string() })
        .nullable(),
    productArea: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Internal create payload (UI). */
export const CreateTicketInput = z.object({
    type: TicketType,
    title: z.string().min(1).max(500),
    description: z.string().optional(),
    priority: Priority.optional(),
    severity: Severity.optional(),
    projectId: z.string().optional(),
    productArea: z.string().optional(),
    component: z.string().optional(),
    repo: z.string().optional(), // GitHub repo ("owner/name"); mandatory in the create UI, optional in the API
    assigneeIds: z.array(z.string()).optional(),
    requesterIds: z.array(z.string()).optional(), // full-replace set of requesters
    customerIds: z.array(z.string()).optional(), // full-replace set of linked customers
    customerId: z.string().optional(),
    customerName: z.string().optional(),
    promiseId: z.string().optional(),
    blockerId: z.string().optional(),
    labels: z.array(z.string()).optional(),
    storyPoints: z.number().optional(),
    estimateHours: z.number().optional(),
    impactArr: z.number().min(0).max(1_000_000_000).optional(),
    impactLtv: z.number().min(0).max(1_000_000_000).optional(),
    impactTcv: z.number().min(0).max(1_000_000_000).optional(),
    dueDate: z.string().optional(),
    parentId: z.string().optional(),
    source: TicketSource.optional(),
});
export const UpdateTicketInput = CreateTicketInput.partial().extend({
    status: TicketStatus.optional(),
    rank: z.string().optional(),
    /** Optimistic concurrency token (updatedAt of the version being edited). */
    expectedUpdatedAt: z.string().optional(),
    // Full-replace set of assignees; [] clears all assignees (unassign).
    assigneeIds: z.array(z.string()).optional(),
    // Full-replace set of requesters; [] clears all requesters.
    requesterIds: z.array(z.string()).optional(),
    // Full-replace set of linked customers; [] unlinks all.
    customerIds: z.array(z.string()).optional(),
    // Nullable so these fields can be CLEARED via inline edit.
    parentId: z.string().nullable().optional(),
    severity: Severity.nullable().optional(),
    sprintId: z.string().nullable().optional(),
    releaseId: z.string().nullable().optional(),
    promiseId: z.string().nullable().optional(),
    storyPoints: z.number().nullable().optional(),
    estimateHours: z.number().nullable().optional(),
    impactArr: z.number().min(0).max(1_000_000_000).nullable().optional(),
    impactLtv: z.number().min(0).max(1_000_000_000).nullable().optional(),
    impactTcv: z.number().min(0).max(1_000_000_000).nullable().optional(),
    projectedArr: z.number().min(0).max(1_000_000_000).nullable().optional(),
    dueDate: z.string().nullable().optional(),
    isLegacy: z.boolean().optional(),
    customSystem: z.boolean().optional(),
    approvedToPromote: z.boolean().optional(),
    blocked: z.boolean().optional(),
    attritionRating: z.number().int().min(1).max(5).nullable().optional(),
    attritionArrLoss: z.number().min(0).nullable().optional(),
    devCount: z.number().int().min(1).optional(),
    attritionNotes: z.string().max(250).nullable().optional(),
    // "Testing & Proof" section (rich HTML with inline screenshots). Nullable to clear.
    testProof: z.string().nullable().optional(),
});
export const TicketTransitionInput = z.object({
    toStatus: TicketStatus,
    comment: z.string().optional(),
    fields: z.record(z.unknown()).optional(),
    override: z.boolean().optional(), // authorized override of blocked transition
});
export const BulkTicketUpdateInput = z.object({
    ticketIds: z.array(z.string()).min(1).max(500),
    patch: z.object({
        status: TicketStatus.optional(),
        assigneeIds: z.array(z.string()).optional(),
        priority: Priority.optional(),
        severity: Severity.nullable().optional(),
        sprintId: z.string().nullable().optional(),
        // `releaseId` is deliberately absent: release membership is owned by
        // ReleaseTicket and goes through POST /releases/:id/tickets(/bulk).
        // Advertising it here meant callers set it and it was silently ignored.
        // Parent/epic: set a parent (epic) for the selected tickets, or null to detach.
        parentId: z.string().nullable().optional(),
        // Set the GitHub repo ("owner/name") on the selected tickets.
        repo: z.string().optional(),
        // Archive/unarchive the selected tickets (moves them out of active views).
        archived: z.boolean().optional(),
        addLabels: z.array(z.string()).optional(),
        removeLabels: z.array(z.string()).optional(),
        addTagIds: z.array(z.string()).optional(),
        removeTagIds: z.array(z.string()).optional(),
    }),
});
/** Filter query for list/search (FR-307). */
export const TicketFilter = z.object({
    engineeringOnly: z.boolean().optional(),
    workspaceKind: z.enum(['product', 'support']).optional(),
    workflowStageId: z.string().optional(),
    workflowOwnerId: z.string().optional(),
    q: z.string().optional(),
    type: z.array(TicketType).optional(),
    status: z.array(TicketStatus).optional(),
    priority: z.array(Priority).optional(),
    severity: z.array(Severity).optional(),
    projectId: z.string().optional(),
    assigneeId: z.string().optional(),
    requesterId: z.string().optional(),
    reporterId: z.string().optional(),
    parentId: z.string().optional(), // direct children of an epic/parent
    sprintId: z.string().optional(),
    releaseId: z.string().optional(),
    label: z.string().optional(),
    customerId: z.string().optional(),
    promiseId: z.string().optional(),
    blockerId: z.string().optional(),
    // Governed tags: filter by tag slug/id. `tagMode` controls match semantics.
    tags: z.array(z.string()).optional(),
    tagMode: z.enum(['any', 'all', 'none']).optional(),
    legacy: z.boolean().optional(), // true = legacy only, false = Tracx only
    // true = archived only, 'all' = archived AND active together, omitted = hide
    // archived (#12). 'all' is what the Done section uses: done work is archived
    // automatically after a week, so hiding it there loses most of the history.
    archived: z.union([z.boolean(), z.literal('all')]).optional(),
    sort: z.string().optional(), // e.g. "-updatedAt", "rank"
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce.number().int().positive().max(250).optional(),
    // Saved view to hydrate from, and the column set the client wants
    // (also used by export so output matches what's on screen).
    viewId: z.string().optional(),
    columnIds: z.array(z.string()).optional(),
});
//# sourceMappingURL=ticket.js.map